/**
 * PublicCvUploadConfig — the Application Link body: shareable link + channel tags.
 *
 * The Application Link is always on: there is no enable toggle. If a job still has
 * the flag off, it is enabled silently once so the shared link accepts CVs.
 * Auto-interview is NOT here anymore — it is a job-level setting (see
 * AutoInterviewSettings / the header gear), applied to every scored candidate.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Copy, Check, Plus, X } from 'lucide-react';

import { JobOffer } from '../../models/job';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/molecules/InputGroup';
import { useCreateSourcingTag, useDeleteSourcingTag, useSourcingTags, useToggleAuthorizeCvUpload } from '../../hooks/useJobOffers';
import { SourcingTagChip } from './SourcingTagChip';

interface PublicCvUploadConfigProps {
  job: JobOffer;
}

interface TagOption {
  slug: string;
  label: string;
}

/** Predefined channels offered in the link builder. */
const CHANNELS: { slug: string; label: string }[] = [
  { slug: 'linkedin', label: 'LinkedIn' },
  { slug: 'indeed', label: 'Indeed' },
  { slug: 'website', label: 'Website' },
];

/** Normalize a free-text tag into a URL-safe slug, matching the backend. */
const slugifySource = (raw: string): string =>
  raw.trim().toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 32);

const MAX_CUSTOM_CHANNELS = 12;
const RESERVED_CHANNELS = new Set(['', 'custom']);

export const PublicCvUploadConfig: React.FC<PublicCvUploadConfigProps> = ({ job }) => {
  const { t } = useTranslation('job');
  const [copied, setCopied] = useState(false);
  const [channel, setChannel] = useState(''); // '' = untagged or a source slug
  const [isAddingCustom, setIsAddingCustom] = useState(false);
  const [customSource, setCustomSource] = useState('');
  const [customChannels, setCustomChannels] = useState<string[]>([]);
  const autoEnabledRef = useRef(false);
  const customInputRef = useRef<HTMLInputElement>(null);

  const toggleAuthorize = useToggleAuthorizeCvUpload(job.id);
  const sourcingTags = useSourcingTags();
  const createSourcingTag = useCreateSourcingTag();
  const deleteSourcingTag = useDeleteSourcingTag();

  // The Application Link is always on: silently enable a job that still has it off.
  useEffect(() => {
    if (!autoEnabledRef.current && job.id && !job.authorizeCvUpload) {
      autoEnabledRef.current = true;
      toggleAuthorize.mutate(true);
    }
  }, [job.id, job.authorizeCvUpload, toggleAuthorize]);

  useEffect(() => {
    if (!isAddingCustom) {
      return;
    }
    customInputRef.current?.focus();
  }, [isAddingCustom]);

  const baseUrl = `${window.location.origin}/job/${job.id}/cv`;
  const effectiveSource = channel;
  const taggedUrl = effectiveSource ? `${baseUrl}?source=${effectiveSource}` : baseUrl;

  useEffect(() => {
    if (!sourcingTags.data) {
      if (sourcingTags.isError) {
        setCustomChannels([]);
      }
      return;
    }

    const sanitized = sourcingTags.data
        .map((value) => slugifySource(value))
        .filter((value, index, array) => Boolean(value) && array.indexOf(value) === index)
        .filter((value) => !RESERVED_CHANNELS.has(value))
        .filter((value) => !CHANNELS.some((defaultChannel) => defaultChannel.slug === value))
        .slice(0, MAX_CUSTOM_CHANNELS);

    setCustomChannels(sanitized);
  }, [sourcingTags.data, sourcingTags.isError]);

  const persistCustomChannel = useCallback(
    async (source: string): Promise<string | null> => {
      const normalized = slugifySource(source);
      if (!normalized) return null;
      if (RESERVED_CHANNELS.has(normalized)) return null;
      if (CHANNELS.some((defaultChannel) => defaultChannel.slug === normalized)) {
        return null;
      }

      try {
        const createdSlug = await createSourcingTag.mutateAsync(source);
        return createdSlug;
      } catch {
        const next = [...customChannels.filter((item) => item !== normalized), normalized].slice(-MAX_CUSTOM_CHANNELS);
        setCustomChannels(next);
        return normalized;
      }
    },
    [createSourcingTag, customChannels]
  );

  const handleCopy = useCallback(async () => {
    navigator.clipboard.writeText(taggedUrl);
    setCopied(true);
    toast.success(t('sourcing.toast_link_copied'));
    setTimeout(() => setCopied(false), 2000);
  }, [taggedUrl, t]);

  const handleAddCustomTag = useCallback(async () => {
    if (!customSource.trim()) {
      setIsAddingCustom(false);
      setCustomSource('');
      return;
    }

    const createdChannel = await persistCustomChannel(customSource);
    if (!createdChannel) {
      return;
    }
    setChannel(createdChannel);
    setCustomSource('');
    setIsAddingCustom(false);
  }, [customSource, persistCustomChannel]);

  const handleCancelAddCustom = useCallback(() => {
    setIsAddingCustom(false);
    setCustomSource('');
  }, []);

  const handleDeleteCustomTag = useCallback(
    async (slug: string) => {
      try {
        await deleteSourcingTag.mutateAsync(slug);
        if (channel === slug) {
          setChannel('');
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : t('sourcing.toast_update_failed');
        toast.error(t('sourcing.toast_update_failed', { error: message }));
      }
    },
    [channel, deleteSourcingTag, t]
  );

  const customChannelSet = new Set(customChannels);
  const tagOptions: TagOption[] = [
    { slug: '', label: t('sourcing.channel_untagged') },
    ...CHANNELS,
    ...customChannels.map((slug) => ({ slug, label: slug })),
  ];

  return (
    <div>
      {/* Shareable link + copy — InputGroup (same family as Manatal / Topbar). */}
      <InputGroup size="sm" className="w-full">
        <InputGroupInput
          readOnly
          value={taggedUrl}
          rounded="md"
          endAddonPadding="icon"
          aria-label={t('sourcing.method_link_title')}
          className="text-dark-invirtus/80"
          onClick={(e) => (e.target as HTMLInputElement).select()}
        />
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            type="icon"
            intent="muted"
            icon={
              copied ? (
                <Check className="h-3.5 w-3.5 text-green" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )
            }
            onClick={handleCopy}
            aria-label={t('sourcing.copy')}
            title={copied ? t('sourcing.copied') : t('sourcing.copy')}
          />
        </InputGroupAddon>
      </InputGroup>

      <div className="mt-2.5">
        <div className="flex flex-wrap items-center gap-1.5 pb-1">
          {tagOptions.map((tag) => (
            <SourcingTagChip
              key={tag.slug || 'untagged'}
              active={channel === tag.slug}
              isCustomTag={customChannelSet.has(tag.slug)}
              label={tag.label}
              slug={tag.slug}
              onDelete={handleDeleteCustomTag}
              onSelect={setChannel}
              deleteLabel={t('sourcing.delete_tag')}
            />
          ))}

          {!isAddingCustom && (
            <button
              type="button"
              onClick={() => setIsAddingCustom(true)}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-light-lila/60 bg-light text-dark-invirtus/70 transition-colors hover:border-light-lila hover:text-dark-invirtus"
              title={t('sourcing.add_tag')}
              aria-label={t('sourcing.add_tag')}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          )}

          {isAddingCustom && (
            <div className="inline-flex h-6 items-center gap-1 rounded-full border border-light-lila/60 bg-light pl-2 pr-1">
              <input
                ref={customInputRef}
                type="text"
                value={customSource}
                onChange={(e) => setCustomSource(e.target.value)}
                onBlur={() => {
                  handleAddCustomTag();
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleAddCustomTag();
                    return;
                  }
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    handleCancelAddCustom();
                  }
                }}
                placeholder={t('sourcing.channel_placeholder')}
                maxLength={32}
                className="h-5 w-18 border-none bg-transparent font-body text-xs text-dark-invirtus placeholder:text-dark-invirtus/35 focus:outline-none"
              />
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleCancelAddCustom}
                title={t('sourcing.cancel_add_tag')}
                aria-label={t('sourcing.cancel_add_tag')}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full text-dark-invirtus/55 transition-colors hover:bg-dark-invirtus-soft hover:text-dark-invirtus"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicCvUploadConfig;
